import { useContext } from 'react';
import { AppContext } from '@ctsu/loader';



export const RolesMap = {
  CTMS_REGISTRAR: "CTMS Registrar",
  LPO_REGISTRAR: "LPO Registrar",
  CTSU_ADMINISTRATOR: "CTSU Administrator",
  SYS_ADMIN: "CTSU Portal System Administrator",
  PROTOCOL_PRINCIPAL_INVESTIGATOR: "Protocol Principal Investigator",
  PROTOCOL_CLIENT_ADMINISTRATOR: "Protocol Client Administrator"
};

export const getCTMSLAOList = (appConfig = {}) => {
  try {
    const ctms = JSON.parse(appConfig?.CTMS_LPO_LIST);
    return ctms?.lpoList;
  } catch (error) {
    return [];
  }
};


export const useValidateUserRole = (roles) => {
  const context = useContext(AppContext);
  const { session } = context;
  const userRoles = session?.userSiteRolePermissions?.map(siteRole => siteRole?.roles?.map(r => r?.displayName?.trim()))?.flat() || [];
  return userRoles?.some(role => roles.map(r => r.toLowerCase()).includes(role.toLowerCase()));
};


export const useCanEditProtocol = (protocolLeadLao) => {

  const context = useContext(AppContext);
  const { session, application } = context;

  const ctmsLaoList = getCTMSLAOList(application?.appConfig)

  const userRoles = session?.userSiteRolePermissions
    ?.map(siteRole => siteRole?.roles?.map(r => r?.displayName?.trim()))
    ?.flat() || [];

  // Get ALL user LAO groups
  const userLaoGroups = session?.userGroups || [];

  const isCtmsRegistrar = userRoles.some(role =>
    role?.toLowerCase() === RolesMap.CTMS_REGISTRAR.toLowerCase()
  );

  // CTMS Registrar can edit protocols led by LAOs in the CTMS list
  if (isCtmsRegistrar) {
    return ctmsLaoList.includes(protocolLeadLao);
  }

  const isProtocolPI = userRoles.some(role =>
    role?.toLowerCase() === RolesMap.PROTOCOL_PRINCIPAL_INVESTIGATOR.toLowerCase()
  );

  // Protocol PI can edit if the protocol's lead LAO matches their LAO affiliation
  if (isProtocolPI && protocolLeadLao) {
    return userLaoGroups.includes(protocolLeadLao);
  }
  // for all other role use PE to decide is user can edit or not hence returns true isntead of false
  return true;
};

/**
 * Gets the highest priority role from a user's roles
 * @param {Array<string>} userRoles - Array of user role display names
 * @returns {string|null} The highest priority role or null if no matching role found
 */
export const getHighestPriorityRole = (userRoles = []) => {
  // Priority order (higher index = higher priority)
  const rolePriority = [
    RolesMap.PROTOCOL_CLIENT_ADMINISTRATOR,
    RolesMap.PROTOCOL_PRINCIPAL_INVESTIGATOR,
    RolesMap.CTMS_REGISTRAR,
    RolesMap.SYS_ADMIN,
    RolesMap.CTSU_ADMINISTRATOR
  ];

  // Find the highest priority role that the user has
  for (let i = rolePriority.length - 1; i >= 0; i--) {
    const priorityRole = rolePriority[i];
    if (userRoles.some(role => role?.toLowerCase() === priorityRole.toLowerCase())) {
      return priorityRole;
    }
  }

  return null;
};

/**
 * Determines which fields are editable based on user role and activation status
 * @param {boolean} isActivated - Whether the protocol has been activated (Activated On is not null)
 * @returns {object} Object with field names as keys and boolean editability as values
 */
export const useEditableArmsFields = (isActivated = false) => {
  const context = useContext(AppContext);
  const { session } = context;
  const userRoles = session?.userSiteRolePermissions?.map(siteRole => siteRole?.roles?.map(r => r?.displayName?.trim()))?.flat() || [];

  // Default - no fields are editable
  let editableFields = {
    stratumName: false,
    description: false,
    status: false,
    statusReason: false,
    accrualTarget: false,
    currentAccrual: false,
    canAddArm: false
  };

  const highestPriorityRole = getHighestPriorityRole(userRoles);

  // Check each role and apply permissions
  if (highestPriorityRole) {

    const roleLower = highestPriorityRole.toLowerCase();

    if (roleLower === RolesMap.CTSU_ADMINISTRATOR.toLowerCase()) {
      editableFields = {
        stratumName: true,
        description: true,
        status: true,
        statusDate: false,
        statusReason: true,
        accrualTarget: true,
        currentAccrual: false,
        canAddArm: true
      };
    } else if (roleLower === RolesMap.SYS_ADMIN.toLowerCase()) {
      editableFields = {
        stratumName: true,
        description: true,
        status: true,
        statusDate: false,
        statusReason: true,
        accrualTarget: true,
        currentAccrual: false,
        canAddArm: true
      };
    } else if (roleLower === RolesMap.CTMS_REGISTRAR.toLowerCase()) {
      if (!isActivated) {
        editableFields = {
          ...editableFields,
          stratumName: true,
          description: true,
          status: true,
          statusDate: false,
          statusReason: true,
          accrualTarget: true,
          currentAccrual: true,
          canAddArm: true
        };
      } else {
        editableFields = {
          ...editableFields,
          status: true,
          statusReason: true,
          statusDate: false,
          accrualTarget: true,
          currentAccrual: true,
          canAddArm: false
        };
      }
    } else if (roleLower === RolesMap.PROTOCOL_PRINCIPAL_INVESTIGATOR.toLowerCase()) {
      editableFields = {
        ...editableFields,
        status: true,
        statusDate: false,
        statusReason: true,
        accrualTarget: true,
        canAddArm: false
      };
    } else if (roleLower === RolesMap.PROTOCOL_CLIENT_ADMINISTRATOR.toLowerCase()) {
      editableFields = {
        ...editableFields,
        status: true,
        statusDate: false,
        statusReason: true,
        accrualTarget: true,
        canAddArm: false
      };
    }
  }

  return editableFields;
};

/**
 * Determines which cohort fields are editable based on user role and protocol activation status
 * @param {boolean} isActivated - Whether the protocol has been activated (Activated On is not null)
 * @returns {object} Object with field names as keys and boolean editability as values
 */
export const useEditableCohortFields = (isActivated = false) => {
  const context = useContext(AppContext);
  const { session } = context;
  const userRoles = session?.userSiteRolePermissions?.map(siteRole => siteRole?.roles?.map(r => r?.displayName?.trim()))?.flat() || [];
  // Default - no fields are editable
  let editableFields = {
    cohortName: false,
    description: false,
    status: false,
    statusReason: false,
    accrualTarget: false,
    canAddCohort: false
  };

  const highestPriorityRole = getHighestPriorityRole(userRoles);

  if (highestPriorityRole) {
    const roleLower = highestPriorityRole.toLowerCase();

    if (roleLower === RolesMap.CTSU_ADMINISTRATOR.toLowerCase()) {
      editableFields = {
        cohortName: true,
        description: true,
        status: true,
        statusReason: true,
        accrualTarget: true,
        canAddCohort: true
      };
    } else if (roleLower === RolesMap.SYS_ADMIN.toLowerCase()) {
      editableFields = {
        cohortName: true,
        description: true,
        status: true,
        statusReason: true,
        accrualTarget: true,
        canAddCohort: true
      };
    } else if (roleLower === RolesMap.CTMS_REGISTRAR.toLowerCase()) {
      if (!isActivated) {
        editableFields = {
          cohortName: true,
          description: true,
          status: true,
          statusReason: true,
          accrualTarget: true,
          currentAccrual: true,
          canAddCohort: true
        };
      } else {
        editableFields = {
          cohortName: false,
          description: false,
          status: true,
          statusReason: true,
          accrualTarget: false,
          currentAccrual: true,
          canAddCohort: false
        };
      }
    } else if (roleLower === RolesMap.PROTOCOL_PRINCIPAL_INVESTIGATOR.toLowerCase()) {
      editableFields = {
        cohortName: false,
        description: false,
        status: true,
        statusReason: true,
        accrualTarget: true,
        canAddCohort: false
      };
    } else if (roleLower === RolesMap.PROTOCOL_CLIENT_ADMINISTRATOR.toLowerCase()) {
      editableFields = {
        cohortName: false,
        description: false,
        status: true,
        statusReason: true,
        accrualTarget: true,
        canAddCohort: false
      };
    }
  }

  return editableFields;
};
